import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldOff, ShieldQuestion, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";

// One place for how an NDA state looks: colour, icon, short label.
export const NDA_TONE = {
  active: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck, label: "NDA active" },
  expiring: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: ShieldAlert, label: "NDA expiring" },
  expired: { cls: "bg-red-50 text-red-700 border-red-200", Icon: ShieldOff, label: "NDA expired" },
  sent: { cls: "bg-red-50 text-red-700 border-red-200", Icon: ShieldQuestion, label: "NDA sent, not executed" },
  none: { cls: "bg-red-50 text-red-700 border-red-200", Icon: ShieldOff, label: "No NDA" },
  waived: { cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: ShieldCheck, label: "NDA waived" },
  not_required: { cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: ShieldCheck, label: "NDA not required" },
  overridden: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: ShieldAlert, label: "NDA gate overridden" },
};

export const ndaBlocks = (state) => ["none", "sent", "expired"].includes(state);

export function NdaMark({ state, testid, label = false }) {
  const t = NDA_TONE[state];
  if (!t) return null;
  return (
    <span data-testid={testid} title={t.label}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${t.cls}`}>
      <t.Icon className="w-2.5 h-2.5" />{label && t.label}
    </span>
  );
}

export function NdaBadge({ summary, testid }) {
  if (!summary) return null;
  const t = NDA_TONE[summary.state] || NDA_TONE.none;
  const tail = summary.state === "active" || summary.state === "expiring" ? (summary.expires_at ? ` · expires ${fmtDate(summary.expires_at)}` : "") : "";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border ${t.cls}`}>
      <t.Icon className="w-3.5 h-3.5" />{t.label}{tail}
    </span>
  );
}

// Red banner on the client, its leads and its bids while the gate is closed.
export function NdaBanner({ summary, clientName, testid = "nda-banner", what = "this client" }) {
  if (!summary || !ndaBlocks(summary.state) || summary.override) return null;
  const why = summary.state === "expired" ? `The NDA with ${what} expired ${fmtDate(summary.expires_at)}.`
    : summary.state === "sent" ? `The NDA with ${what} is out for signature but not executed.` : `There is no NDA on file with ${what}.`;
  return (
    <div data-testid={testid} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      <ShieldOff className="w-4 h-4 shrink-0" />
      <span className="font-semibold">{why}</span>
      <span className="text-red-700/80">Sending the bid, share links and proposal emails stay blocked until it is executed. An owner or admin can override with a reason.</span>
      {clientName && <Link to={`/clients/${encodeURIComponent(clientName)}?tab=documents`} data-testid={`${testid}-file-link`} className="underline font-medium ml-auto">File the NDA</Link>}
    </div>
  );
}

// 409 nda_required → ask the owner/admin for a reason and retry with it. Call `onRetry(reason)`.
export function NdaOverrideDialog({ gate, onClose, onRetry }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!gate) return null;
  const go = async () => { setBusy(true); try { await onRetry(reason.trim()); onClose(); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="nda-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldOff className="w-4 h-4 text-red-600" /> No active NDA</DialogTitle>
          <DialogDescription data-testid="nda-override-message">{gate.message}</DialogDescription>
        </DialogHeader>
        {gate.can_override ? (
          <>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="nda-override-reason"
              placeholder="Why it is safe to proceed without an NDA (logged on the bid)…" />
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="nda-override-cancel">Cancel</Button>
              <Button onClick={go} disabled={busy || reason.trim().length < 5} className="bg-red-600 hover:bg-red-700 text-white" data-testid="nda-override-confirm">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Override and continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600" data-testid="nda-override-denied">Ask an owner or admin to file the NDA or override the gate.</p>
            <DialogFooter>
              {gate.client_name && <Button asChild variant="outline"><Link to={`/clients/${encodeURIComponent(gate.client_name)}?tab=documents`}>Open client documents</Link></Button>}
              <Button onClick={onClose} data-testid="nda-override-close">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const ndaGateOf = (e) => { const d = e?.response?.data?.detail; return d && typeof d === "object" && d.code === "nda_required" ? d : null; };

// Asked before a bid upload when the client has no active NDA: was this file received under one?
export function ReceivedUnderNdaDialog({ open, filename, onAnswer }) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onAnswer(null); }}>
      <DialogContent className="max-w-md" data-testid="received-under-nda-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldQuestion className="w-4 h-4 text-amber-600" /> Received under NDA?</DialogTitle>
          <DialogDescription>This client has no active NDA on file. Was <b>{filename}</b> received under one? The answer is kept on the document.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onAnswer(false)} data-testid="received-under-nda-no">No</Button>
          <Button onClick={() => onAnswer(true)} data-testid="received-under-nda-yes">Yes, under NDA</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
